import { TrackEvent } from '@enums';
import { TrackFn } from '@lib/analytics/types';

type SendEventCallback = (
    eventName: string,
    data: Record<string, unknown>
) => void;

interface VideoSourceMetadata {
    src: string;
    mimeType: string | null;
    extension: string | null;
    sizeMB: number | null;
}

interface VideoMetadata {
    sources: VideoSourceMetadata[];
    activeSource: VideoSourceMetadata | null;
    resolution: {
        width: number;
        height: number;
    };
}

export class VideoAnalytics {
    private videoElement: HTMLVideoElement;
    private sendEvent: SendEventCallback;
    private bufferingStartTime: number | null = null;
    private totalBufferingDuration: number = 0;
    private cachedMetadata: VideoMetadata | null = null;
    private isInitialLoad: boolean = true;

    constructor(videoElement: HTMLVideoElement, track: TrackFn) {
        this.videoElement = videoElement;
        this.sendEvent = (event, data) =>
            track(TrackEvent.VideoEvent, { videoEvent: event, ...data });
        this.attachEventListeners();
        this.getCachedMetadata();
    }

    private attachEventListeners() {
        this.videoElement.addEventListener(
            'waiting',
            this.onLoadStart.bind(this)
        );
        this.videoElement.addEventListener(
            'playing',
            this.onLoadEnd.bind(this)
        );
        this.videoElement.addEventListener('error', this.onError.bind(this));
    }

    private onLoadStart() {
        if (this.bufferingStartTime === null) {
            this.bufferingStartTime = performance.now();
        }
    }
    private async onLoadEnd() {
        if (this.bufferingStartTime !== null) {
            const bufferingDuration =
                performance.now() - this.bufferingStartTime;
            this.totalBufferingDuration += bufferingDuration;
            if (bufferingDuration === 0) return;
            this.bufferingStartTime = null;
            this.isInitialLoad = false;
            const metadata = await this.getCachedMetadata();
            this.sendEvent('load', {
                bufferingDuration,
                totalBufferingDuration: this.totalBufferingDuration,
                isInitialLoad: this.isInitialLoad,
                ...metadata,
            });
        }
    }

    private async onError(event: Event) {
        const error = (event.target as HTMLVideoElement).error;
        if (error) {
            const metadata = await this.getCachedMetadata();
            this.sendEvent('player error', {
                code: error.code,
                message: error.message,
                ...metadata,
            });
        }
    }

    private async getCachedMetadata(): Promise<VideoMetadata> {
        if (!this.cachedMetadata) {
            this.cachedMetadata = await this.retrieveVideoMetadata();
        }
        return this.cachedMetadata;
    }

    private async retrieveVideoMetadata(): Promise<VideoMetadata> {
        const sources = await Promise.all(
            Array.from(this.videoElement.querySelectorAll('source')).map(
                async (source) => {
                    const src = source.src;
                    const mimeType = source.type || null;
                    const extension = this.getFileExtension(src);
                    const sizeMB = await this.getFileSizeMB(src);
                    return { src, mimeType, extension, sizeMB };
                }
            )
        );

        const activeSource =
            sources.find(
                (source) => source.src === this.videoElement.currentSrc
            ) || null;

        const resolution = {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight,
        };

        return { sources, activeSource, resolution };
    }

    private async getFileSizeMB(url: string): Promise<number> {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            if (!response.ok) {
                this.sendEvent('source error', {
                    url,
                    code: response.status,
                    response: await response.text(),
                });
            }
            const contentLength = Number(
                response.headers.get('Content-Length')
            );
            return contentLength ? contentLength / (1024 * 1024) : 0;
        } catch (error) {
            return 0;
        }
    }

    private getFileExtension(url: string): string | null {
        const match = url.match(/\.(\w+)(\?.*)?$/);
        return match ? match[1] : null;
    }

    public async sendMediaEvent(event: string) {
        const metadata = await this.getCachedMetadata();
        this.sendEvent(event, { ...metadata });
    }
}
